/**
 * Rolling 12-month breakdown tables shared between Executive BI and Dashboard.
 * Renders the salaried-under-target callout + 3 count tables (Referrals /
 * Evals / Therapy Starts) + the Ref→Eval Conversion rate table.
 *
 * The former "Client Outcomes by Month" table was removed per product request
 * — Davinder confirmed outcomes are tracked directly in the Pipeline UI now.
 */
import { useEffect, useState, Fragment } from 'react';
import api from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { Card } from './ui/card';
import { fmtDate, fmtMonthYear } from '../lib/date';
import { ChevronDown, ChevronRight } from 'lucide-react';
import InfoTooltip from './InfoTooltip';
import { useDrilldown } from './DrillDownDrawer';

const C = {
  ink: '#2D2833',
  soft: '#685B72',
  border: '#E8E5EC',
};

function UnderTargetCallout({ clinic = null } = {}) {
  const [flags, setFlags] = useState(null);
  useEffect(() => {
    const params = clinic ? { clinic } : {};
    api.get('/performance/under-target-flags', { params })
      .then(r => setFlags(r.data))
      .catch(() => setFlags(null));
  }, [clinic]);
  if (!flags || !flags.flags?.length) return null;
  return (
    <Card className="rounded-2xl border-2 p-5" style={{ borderColor: '#8A3A3A', background: '#F5EAEA' }} data-testid="under-target-callout">
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: '#8A3A3A' }}>Salaried under-target · trailing 4-week window{clinic ? ` · ${clinic}` : ''}</div>
          <div className="font-serif text-lg mt-0.5" style={{ color: '#2D2833' }}>{flags.flags.length} clinician{flags.flags.length !== 1 ? 's' : ''} flagged</div>
          <div className="text-[11px] mt-0.5" style={{ color: '#685B72' }}>Window: {fmtDate(flags.window_start)} → {fmtDate(flags.window_end)} · Rule: 4-week rolling average &lt; 95% of target. Streak and cumulative shortfall are shown separately so 3-of-4 weeks under target isn't mislabelled as a full-window streak.</div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[12px]">
          <thead>
            <tr style={{ background: '#EED9D9' }}>
              <th className="px-3 py-2 text-left font-semibold" style={{ color: '#8A3A3A' }}>Clinician</th>
              <th className="px-3 py-2 text-left font-semibold" style={{ color: '#8A3A3A' }}>Role</th>
              <th className="px-3 py-2 text-right font-semibold" style={{ color: '#8A3A3A' }}>Target</th>
              {flags.weeks.map(w => <th key={w} className="px-3 py-2 text-center font-semibold" style={{ color: '#8A3A3A' }}>Wk of {fmtDate(w)}</th>)}
              <th className="px-3 py-2 text-center font-semibold" style={{ color: '#8A3A3A' }} title="How many of the last four weeks were strictly under target.">Weeks under (of 4)</th>
              <th className="px-3 py-2 text-center font-semibold" style={{ color: '#8A3A3A' }} title="Longest run of consecutive under-target weeks inside the trailing 4-week window.">Consecutive streak</th>
              <th className="px-3 py-2 text-center font-semibold" style={{ color: '#8A3A3A' }}>12-wk trend</th>
              <th className="px-3 py-2 text-right font-semibold" style={{ color: '#8A3A3A' }} title="Cumulative shortfall across the trailing 4 weeks — sum of (target − shows) per week, regardless of streak.">Cumulative shortfall</th>
              <th className="px-3 py-2 text-left font-semibold" style={{ color: '#8A3A3A' }}>Reports to</th>
            </tr>
          </thead>
          <tbody>
            {flags.flags.map(f => {
              // Recompute streak/count client-side when the backend
              // hasn't populated the new fields yet (backward compat).
              const _under = (f.weekly_shows || []).filter(n => n < (f.weekly_target || 0)).length;
              let _streak = 0, _best = 0;
              (f.weekly_shows || []).forEach(n => {
                if (n < (f.weekly_target || 0)) { _streak += 1; if (_streak > _best) _best = _streak; }
                else { _streak = 0; }
              });
              const weeksUnder = f.weeks_under_of_4 ?? _under;
              const streak = f.consecutive_streak_under ?? _best;
              return (
                <tr key={f.clinician} className="border-t" style={{ borderColor: '#8A3A3A' }} data-testid={`under-target-row-${f.clinician}`}>
                  <td className="px-3 py-2 font-medium" style={{ color: '#2D2833' }}>{f.clinician}</td>
                  <td className="px-3 py-2" style={{ color: '#685B72' }}>{f.role}</td>
                  <td className="px-3 py-2 text-right font-mono">{f.weekly_target}</td>
                  {f.weekly_shows.map((n, i) => (
                    <td key={i} className="px-3 py-2 text-center font-mono" style={{ color: n < f.weekly_target ? '#8A3A3A' : '#839E97', fontWeight: 600 }}>{n}</td>
                  ))}
                  <td className="px-3 py-2 text-center font-mono font-semibold" style={{ color: weeksUnder >= 3 ? '#8A3A3A' : '#B08A3A' }} data-testid={`weeks-under-${f.clinician}`}>{weeksUnder} of 4</td>
                  <td className="px-3 py-2 text-center font-mono font-semibold" style={{ color: streak >= 4 ? '#8A3A3A' : streak >= 2 ? '#B08A3A' : '#685B72' }} data-testid={`streak-${f.clinician}`}>
                    {streak === 4 ? '4 in a row' : streak === 0 ? '—' : `${streak}`}
                  </td>
                  <td className="px-3 py-2 text-center" data-testid={`under-target-sparkline-${f.clinician}`}>
                    <UnderTargetSparkline
                      values={f.trend_shows_12 || f.weekly_shows}
                      target={f.weekly_target}
                      weeksIso={f.trend_weeks_iso_12 || f.weeks_iso}
                    />
                  </td>
                  <td className="px-3 py-2 text-right font-mono font-bold" style={{ color: '#8A3A3A' }}>-{f.shortfall}</td>
                  <td className="px-3 py-2 text-[11px]" style={{ color: '#685B72' }}>{f.od_name || '(no OD assigned)'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// Iter 225 — compact SVG sparkline for the under-target callout.
// Renders the clinician's weekly Show counts across the 12-week window
// with a horizontal reference line at their target. Bars/dots colour
// switches based on whether that week hit target. Deliberately minimal
// — no axis, no numbers — the surrounding cells already carry precise
// figures; this is a "trajectory at a glance" cue.
function UnderTargetSparkline({ values = [], target = 0, weeksIso = [] }) {
  if (!values || !values.length) return <span className="text-[#685B72] text-[11px]">—</span>;
  const W = 140, H = 32, pad = 3;
  const max = Math.max(target || 0, ...values, 1);
  const stepX = (W - 2 * pad) / Math.max(values.length - 1, 1);
  const scaleY = (v) => H - pad - ((v / max) * (H - 2 * pad));
  const points = values.map((v, i) => `${pad + i * stepX},${scaleY(v)}`).join(' ');
  const targetY = scaleY(target || 0);
  return (
    <svg width={W} height={H} className="inline-block align-middle" role="img" aria-label={`12-week trend vs target ${target}`}>
      {/* Target line */}
      {target > 0 && (
        <line x1={pad} y1={targetY} x2={W - pad} y2={targetY}
              stroke="#B08A3A" strokeWidth="1" strokeDasharray="2,2" opacity="0.7" />
      )}
      {/* Trajectory */}
      <polyline points={points} fill="none" stroke="#8A3A3A" strokeWidth="1.5" />
      {/* Weekly dots — green when >= target, red otherwise */}
      {values.map((v, i) => (
        <circle key={i} cx={pad + i * stepX} cy={scaleY(v)} r="2"
                fill={v >= (target || 0) ? '#839E97' : '#8A3A3A'}>
          <title>{`${weeksIso[i] ? `Wk of ${weeksIso[i]}` : ''}: ${v} Shows${target ? ` (target ${target})` : ''}`}</title>
        </circle>
      ))}
    </svg>
  );
}

const MoMChange = ({ n, pct, isProjection }) => {
  const label = isProjection ? ' proj' : '';
  const pctStr = (pct === 0 || pct) ? ` (${pct > 0 ? '+' : ''}${pct}%)` : '';
  if (!n) return <span style={{ color: '#685B72' }}>→ 0{pctStr}{label && <span className="text-[10px] opacity-70">{label}</span>}</span>;
  if (n > 0) return <span style={{ color: '#839E97' }}>▲ {n}{pctStr}{label && <span className="text-[10px] opacity-70">{label}</span>}</span>;
  return <span style={{ color: '#8A3A3A' }}>▼ {Math.abs(n)}{pctStr}{label && <span className="text-[10px] opacity-70">{label}</span>}</span>;
};

function CountTable({ title, tone, tbl, testId, description, tooltip, metric, attribution }) {
  const { openDrilldown } = useDrilldown();
  const drill = (clinic, monthKey) => (e) => {
    e.stopPropagation();
    if (!metric) return;   // safety: no metric wired → non-clickable table
    openDrilldown({
      title: `${title} — ${fmtMonthYear(monthKey + '-01')}`,
      subtitle: `${clinic}${attribution && attribution !== 'main' ? ` · ${attribution} clinic` : ''}`,
      endpoint: '/drilldown/referral-cohort',
      params: {
        month: monthKey,
        metric,
        scope: clinic,
        clinic_attribution: attribution || 'main',
      },
      rowNav: (r) => (r?.client_id ? `/pipeline?open=${r.client_id}` : null),
    });
  };
  return (
    <Card className="rounded-2xl border p-0 overflow-hidden" style={{ borderColor: C.border }} data-testid={testId}>
      <div className="px-4 py-2 flex items-center gap-2 text-white" style={{ background: tone }}>
        <span className="text-[10px] uppercase tracking-widest font-bold">{title}</span>
        <span className="text-[10px] opacity-70">(12 Months rolling · click a count to see the underlying clients)</span>
        {tooltip && <InfoTooltip testId={`tt-${testId}`} text={tooltip} className="text-white/70 hover:text-white" />}
      </div>
      {description && (
        <p className="px-4 py-2 text-[11px]" style={{ color: '#685B72', background: '#FCFBFD' }} data-testid={`${testId}-desc`}>
          {description}
        </p>
      )}
      {tbl.months.length === 0 ? (
        <div className="p-4 text-center text-[12px]" style={{ color: '#685B72' }}>No data yet in this window.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead>
              <tr style={{ background: '#F1EDF5' }}>
                <th className="px-3 py-2 text-left font-semibold" style={{ color: '#685B72' }}>Clinic</th>
                {tbl.months.map(m => <th key={m} className="px-3 py-2 text-center font-semibold whitespace-nowrap" style={{ color: '#685B72' }}>{fmtMonthYear(m + '-01')}</th>)}
                <th className="px-3 py-2 text-center font-semibold whitespace-nowrap" style={{ color: '#685B72' }} title={tbl.latest_is_current ? 'Current month is projected forward based on days elapsed so the comparison is fair against last month.' : 'Latest month vs previous month.'}>MoM Change</th>
                <th className="px-3 py-2 text-center font-semibold whitespace-nowrap" style={{ color: '#685B72' }} title="This month vs the same month one year ago. Shows a dash when we don't yet have prior-year data.">YoY Change</th>
              </tr>
            </thead>
            <tbody>
              {tbl.clinics.map(row => (
                <tr key={row.clinic} className="border-t" style={{ borderColor: '#E8E5EC' }}>
                  <td className="px-3 py-2 font-medium" style={{ color: '#2D2833' }}>{row.clinic}</td>
                  {row.values.map((v, i) => {
                    const canDrill = metric && v > 0;
                    return (
                      <td
                        key={i}
                        className={`px-3 py-2 text-center font-mono ${canDrill ? 'cursor-pointer underline decoration-dotted underline-offset-2 hover:decoration-solid hover:bg-[#FBF8F4]' : ''}`}
                        onClick={canDrill ? drill(row.clinic, tbl.months[i]) : undefined}
                        data-testid={canDrill ? `${testId}-drill-${row.clinic.replace(/\W+/g, '_')}-${tbl.months[i]}` : undefined}
                        title={canDrill ? 'Click to see the clients contributing to this count' : undefined}
                      >
                        {v}
                      </td>
                    );
                  })}
                  <td className="px-3 py-2 text-center font-mono"><MoMChange n={row.trend} pct={row.mom_pct} isProjection={row.is_projection} /></td>
                  <td className="px-3 py-2 text-center font-mono">
                    {row.has_yoy ? <MoMChange n={row.yoy} pct={row.yoy_pct} isProjection={row.is_projection} /> : <span style={{ color: '#C4BCC9' }}>—</span>}
                  </td>
                </tr>
              ))}
              <tr style={{ background: '#685B72', color: 'white' }}>
                <td className="px-3 py-2 font-bold">ALL</td>
                {tbl.totals.map((v, i) => (
                  <td
                    key={i}
                    className={`px-3 py-2 text-center font-mono font-bold ${metric && v > 0 ? 'cursor-pointer underline decoration-dotted underline-offset-2 hover:decoration-solid' : ''}`}
                    onClick={metric && v > 0 ? (e) => { e.stopPropagation(); openDrilldown({
                      title: `${title} — ${fmtMonthYear(tbl.months[i] + '-01')}`,
                      subtitle: `All clinics${attribution && attribution !== 'main' ? ` · ${attribution} clinic` : ''}`,
                      endpoint: '/drilldown/referral-cohort',
                      params: { month: tbl.months[i], metric, clinic_attribution: attribution || 'main' },
                      rowNav: (r) => (r?.client_id ? `/pipeline?open=${r.client_id}` : null),
                    }); } : undefined}
                    data-testid={metric && v > 0 ? `${testId}-drill-ALL-${tbl.months[i]}` : undefined}
                  >
                    {v}
                  </td>
                ))}
                <td className="px-3 py-2"></td>
                <td className="px-3 py-2"></td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

export { UnderTargetCallout };

export default function MonthlyTablesSection({ showUnderTarget = false } = {}) {
  const { user } = useAuth();
  const isOD = user?.role === 'office_director';
  // Iter 47 — ODs can toggle between their own clinic's rollup and the
  // full org-wide rollup. Defaults to "mine" so nothing surprises them.
  const [scope, setScope] = useState('mine');
  const [d, setD] = useState(null);
  useEffect(() => {
    const params = { months: 12 };
    if (isOD) params.scope = scope;
    api.get('/dashboard/monthly-tables', { params })
      .then(r => setD(r.data))
      .catch(() => setD(null));
  }, [isOD, scope]);
  if (!d) return null;

  return (
    <div className="space-y-4 mt-6" data-testid="monthly-tables-section">
      {showUnderTarget && <UnderTargetCallout />}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-serif text-2xl" style={{ color: C.ink }}>Monthly Tables</h2>
          <div className="text-[12px]" style={{ color: C.soft }}>Rolling 12-month breakdown by clinic — months without data are hidden so trends read clean.</div>
        </div>
        {isOD && (
          <div className="inline-flex rounded-xl border overflow-hidden text-[11px]" style={{ borderColor: C.border }} data-testid="monthly-scope-toggle">
            <button
              onClick={() => setScope('mine')}
              className={`px-3 py-1.5 font-semibold uppercase tracking-widest transition-colors ${scope === 'mine' ? 'bg-[#685B72] text-white' : 'bg-white text-[#685B72] hover:bg-[#F1EDF5]'}`}
              data-testid="monthly-scope-mine"
            >
              My Location
            </button>
            <button
              onClick={() => setScope('group')}
              className={`px-3 py-1.5 font-semibold uppercase tracking-widest transition-colors border-l ${scope === 'group' ? 'bg-[#685B72] text-white' : 'bg-white text-[#685B72] hover:bg-[#F1EDF5]'}`}
              style={{ borderColor: C.border }}
              data-testid="monthly-scope-group"
            >
              Group-wide
            </button>
          </div>
        )}
      </div>
      <CountTable title="Referrals Received" tone="#685B72" tbl={d.referrals} testId="tbl-referrals" metric="referrals"        attribution="main"    description="This table shows how many new referrals each clinic received per month over the last 12 months, with a trend arrow for month-over-month direction." tooltip="Attributed to the Main Clinic on the client's file. A referral counts once, in the month its Referral Date lands in." />
      <CountTable title="Evals Completed"    tone="#839E97" tbl={d.evals}     testId="tbl-evals"     metric="evals_completed"  attribution="eval"    description="This table shows the number of evaluations completed at each clinic each month — good for measuring how quickly referred families are being seen." tooltip="Attributed to the clinic that actually hosted the eval (Eval Clinic on the client's file). Falls back to Main Clinic when Eval Clinic is blank." />
      <CountTable title="Therapy Started"    tone="#685B72" tbl={d.therapy}   testId="tbl-therapy"   metric="therapy_started"  attribution="therapy" description="This table shows how many families started therapy at each clinic each month, i.e. successful conversion into ongoing care." tooltip="Attributed to the Therapy Clinic on the client's file (where ongoing therapy happens). Falls back to Main Clinic when Therapy Clinic is blank." />

      {/* Cohort-Based True Referral-to-Therapy Conversion (iter 42) — the
          question we actually care about: for every referral we received
          in month X, what % went on to start therapy (regardless of when
          they actually started)? The table + funnel replace the old
          per-clinic Ref→Eval snapshot. */}
      <CohortConversionTable rows={d.cohorts || []} byClinic={d.cohorts_by_clinic || {}} />
      <ConversionFunnel funnel={d.funnel || []} byClinic={d.funnel_by_clinic || {}} />
      <TopFacilitiesPanel facilities={d.top_facilities || []} />
    </div>
  );
}

// ── Top referring facilities (iter 45) ──────────────────────────────────
function TopFacilitiesPanel({ facilities }) {
  const { openDrilldown } = useDrilldown();
  if (!facilities || facilities.length === 0) return null;
  // 12-month rolling window ending at biz_today's month — matches how the
  // rollup itself is computed on the backend.
  const now = new Date();
  const endMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const s = new Date(now.getFullYear(), now.getMonth() - 11, 1);
  const startMonth = `${s.getFullYear()}-${String(s.getMonth() + 1).padStart(2, '0')}`;
  const drill = (f, metric) => (e) => {
    e.stopPropagation();
    openDrilldown({
      title: `${f.facility} — ${metric === 'referrals' ? 'Referrals' : metric === 'evals_scheduled' ? 'Evals scheduled' : metric === 'therapy_started' ? 'Therapy started' : 'Days to start'}`,
      subtitle: `Last 12 months · ${(f.clinics || []).join(', ') || 'All clinics'}`,
      endpoint: '/drilldown/referral-cohort',
      params: { start_month: startMonth, end_month: endMonth, metric, facility: f.facility, clinic_attribution: 'main' },
      rowNav: (r) => (r?.client_id ? `/pipeline?open=${r.client_id}` : null),
    });
  };
  return (
    <Card className="rounded-2xl border p-0 overflow-hidden" style={{ borderColor: C.border }} data-testid="tbl-top-facilities">
      <div className="px-4 py-2 flex items-center gap-2 text-white" style={{ background: '#685B72' }}>
        <span className="text-[10px] uppercase tracking-widest font-bold">Top Referring Facilities</span>
        <span className="text-[10px] opacity-70">(Which outside locations feed the strongest cohorts — click a count to see the exact clients)</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[12px]">
          <thead>
            <tr style={{ background: '#F1EDF5' }}>
              <th className="px-3 py-2 text-center font-semibold" style={{ color: '#685B72' }}>#</th>
              <th className="px-3 py-2 text-right font-semibold" style={{ color: '#685B72' }}>Facility</th>
              <th className="px-3 py-2 text-right font-semibold" style={{ color: '#685B72' }}>Feeds Clinic(s)</th>
              <th className="px-3 py-2 text-center font-semibold" style={{ color: '#685B72' }}>Referrals</th>
              <th className="px-3 py-2 text-center font-semibold" style={{ color: '#685B72' }}>Eval Scheduled %</th>
              <th className="px-3 py-2 text-center font-semibold" style={{ color: '#685B72' }}>Therapy Started %</th>
              <th className="px-3 py-2 text-center font-semibold" style={{ color: '#685B72' }}>Avg. Days to Start</th>
            </tr>
          </thead>
          <tbody>
            {facilities.map((f, i) => (
              <tr key={f.facility} className="border-t" style={{ borderColor: '#E8E5EC' }} data-testid={`facility-row-${i}`}>
                <td className="px-3 py-2 font-mono text-[11px] text-center" style={{ color: '#685B72' }}>{i + 1}</td>
                <td className="px-3 py-2 font-medium text-right" style={{ color: '#2D2833' }}>{f.facility}</td>
                <td className="px-3 py-2 text-[11px] text-right" style={{ color: '#685B72' }}>{(f.clinics || []).join(', ')}</td>
                <td className="px-3 py-2 text-center font-mono cursor-pointer underline decoration-dotted underline-offset-2 hover:decoration-solid" style={{ color: '#2D2833' }} onClick={drill(f, 'referrals')} data-testid={`facility-drill-${i}-referrals`}>{f.referrals}</td>
                <td className="px-3 py-2 text-center font-mono cursor-pointer underline decoration-dotted underline-offset-2 hover:decoration-solid" style={_pctColor(f.evals_scheduled_pct)} onClick={drill(f, 'evals_scheduled')} data-testid={`facility-drill-${i}-evals`}>{f.evals_scheduled_pct}%</td>
                <td className="px-3 py-2 text-center font-mono font-semibold cursor-pointer underline decoration-dotted underline-offset-2 hover:decoration-solid" style={_pctColor(f.therapy_started_pct)} onClick={drill(f, 'therapy_started')} data-testid={`facility-drill-${i}-therapy`}>
                  {f.therapy_started_pct}% <span className="text-[10px] opacity-70">({f.therapy_started}/{f.referrals})</span>
                </td>
                <td className="px-3 py-2 text-center font-mono cursor-pointer underline decoration-dotted underline-offset-2 hover:decoration-solid" style={{ color: '#685B72' }} onClick={drill(f, 'avg_days_to_start')} data-testid={`facility-drill-${i}-days`}>
                  {f.avg_days_to_start == null ? '—' : `${f.avg_days_to_start}d`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ── Cohort table ────────────────────────────────────────────────────────
function _pctColor(pct) {
  if (pct == null) return { background: '#F1EDF5', color: '#685B72' };
  if (pct >= 70) return { background: 'rgba(131,158,151,0.28)', color: '#839E97' };
  if (pct >= 40) return { background: 'rgba(176,138,58,0.28)',  color: '#B08A3A' };
  return { background: 'rgba(138,58,58,0.22)', color: '#8A3A3A' };
}
export function CohortConversionTable({ rows, byClinic }) {
  const [openMonth, setOpenMonth] = useState(null);
  const { openDrilldown } = useDrilldown();
  // Clinic filter (iter 108) — Dashboard passes both org-wide `rows` and
  // per-clinic `byClinic`; picking a clinic swaps in that slice. When
  // rendered on a single Clinic View, only `rows` is passed and the
  // dropdown is hidden.
  const [scope, setScope] = useState('all');
  const clinicOptions = Object.keys(byClinic || {}).sort();
  const visibleRows = scope === 'all' ? rows : (byClinic?.[scope] || []);

  // Open the shared drill-down drawer with the exact filter that produced
  // the clicked cell. `source` scopes to a specific referral bucket for
  // the expanded per-source sub-rows.
  const drill = (row, metric, source = null) => (e) => {
    e.stopPropagation();
    const clinicScope = scope === 'all' ? null : scope;
    const label = {
      referrals: 'Referrals',
      evals_scheduled: 'Evals scheduled',
      therapy_started: 'Therapy started',
      avg_days_to_start: 'Days-to-start',
    }[metric] || metric;
    const subtitleParts = [fmtMonthYear(row.month + '-01')];
    if (clinicScope) subtitleParts.push(clinicScope);
    if (source) subtitleParts.push(source);
    openDrilldown({
      title: `${label} — ${fmtMonthYear(row.month + '-01')}`,
      subtitle: subtitleParts.filter(Boolean).join(' · '),
      endpoint: '/drilldown/referral-cohort',
      params: {
        month: row.month,
        metric,
        ...(clinicScope ? { scope: clinicScope } : {}),
        ...(source ? { source } : {}),
      },
      rowNav: (r) => (r?.client_id ? `/pipeline?open=${r.client_id}` : null),
    });
  };
  return (
    <Card className="rounded-2xl border p-0 overflow-hidden" style={{ borderColor: C.border }} data-testid="tbl-cohort-conversion">
      <div className="px-4 py-2 flex items-center justify-between gap-2 text-white" style={{ background: '#685B72' }}>
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="text-[10px] uppercase tracking-widest font-bold">True Referral-to-Therapy Conversion</span>
          <span className="text-[10px] opacity-70">
            {scope === 'all'
              ? '(Cohort-based — a June referral that starts therapy in July or Aug still counts for June · click a row to see referral-source split)'
              : `(${scope} · cohort-based · click a row to see referral-source split)`}
          </span>
          <InfoTooltip testId="tt-cohort-conversion" text="Cohorts are anchored to the client's Main Clinic — no matter where the eval or therapy actually happened. Each row asks: 'of the referrals that hit us that month, how many went on to schedule an eval, complete the eval, get pre-auth cleared, and eventually start therapy?'" className="text-white/70 hover:text-white" />
        </div>
        {clinicOptions.length > 0 && (
          <select
            value={scope}
            onChange={e => { setOpenMonth(null); setScope(e.target.value); }}
            className="text-[11px] rounded-md px-2 py-1 bg-white/10 text-white border border-white/20 hover:bg-white/20 cursor-pointer"
            data-testid="cohort-scope"
          >
            <option value="all" className="text-[#2D2833]">All clinics (group)</option>
            {clinicOptions.map(cn => (
              <option key={cn} value={cn} className="text-[#2D2833]">{cn}</option>
            ))}
          </select>
        )}
      </div>
      <p className="px-4 py-2 text-[11px]" style={{ color: '#685B72', background: '#FCFBFD' }} data-testid="tbl-cohort-conversion-desc">
        This table follows every family from the month their referral came in, so you see how many of those exact referrals went on to complete an evaluation and eventually started therapy — even if the eval or therapy start happened in a later month.
      </p>
      {visibleRows.length === 0 ? (
        <div className="p-4 text-center text-[12px]" style={{ color: '#685B72' }}>No referral cohorts yet in this 12-month window.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead>
              <tr style={{ background: '#F1EDF5' }}>
                <th className="px-2 py-2 text-center font-semibold w-8" style={{ color: '#685B72' }} />
                <th className="px-3 py-2 text-center font-semibold" style={{ color: '#685B72' }}>Month Received</th>
                <th className="px-3 py-2 text-center font-semibold" style={{ color: '#685B72' }}>Total Referrals</th>
                <th className="px-3 py-2 text-center font-semibold" style={{ color: '#685B72' }}>Evals Scheduled %</th>
                <th className="px-3 py-2 text-center font-semibold" style={{ color: '#685B72' }}>Therapy Started %</th>
                <th className="px-3 py-2 text-center font-semibold w-24" style={{ color: '#685B72' }}>Avg. Days to Start</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map(r => {
                const isOpen = openMonth === r.month;
                const canExpand = (r.by_source || []).length > 0;
                return (
                <Fragment key={r.month}>
                  <tr
                    className="border-t cursor-pointer hover:bg-[#FAF8FB] transition-colors"
                    style={{ borderColor: '#E8E5EC' }}
                    data-testid={`cohort-row-${r.month}`}
                    onClick={() => canExpand && setOpenMonth(isOpen ? null : r.month)}
                  >
                    <td className="px-2 py-2 text-center w-8" style={{ color: '#685B72' }}>
                      {canExpand && (isOpen ? <ChevronDown size={12} className="inline" /> : <ChevronRight size={12} className="inline" />)}
                    </td>
                    <td className="px-3 py-2 text-center font-medium whitespace-nowrap" style={{ color: '#2D2833' }}>{fmtMonthYear(r.month + '-01')}</td>
                    <td className="px-3 py-2 text-center font-mono underline decoration-dotted underline-offset-2 hover:decoration-solid" style={{ color: '#2D2833' }} onClick={drill(r, 'referrals')} data-testid={`cohort-drill-${r.month}-referrals`} title="Show the exact clients">{r.referrals}</td>
                    <td className="px-3 py-2 text-center font-mono underline decoration-dotted underline-offset-2 hover:decoration-solid" style={_pctColor(r.evals_scheduled_pct)} onClick={drill(r, 'evals_scheduled')} data-testid={`cohort-drill-${r.month}-evals`} title="Show the clients that scheduled an eval">
                      {r.evals_scheduled_pct}% <span className="text-[10px] opacity-70">({r.evals_scheduled}/{r.referrals})</span>
                    </td>
                    <td className="px-3 py-2 text-center font-mono font-semibold underline decoration-dotted underline-offset-2 hover:decoration-solid" style={_pctColor(r.therapy_started_pct)} onClick={drill(r, 'therapy_started')} data-testid={`cohort-drill-${r.month}-therapy`} title="Show the clients that started therapy">
                      {r.therapy_started_pct}% <span className="text-[10px] opacity-70">({r.therapy_started}/{r.referrals})</span>
                    </td>
                    <td className="px-3 py-2 text-center font-mono w-24 underline decoration-dotted underline-offset-2 hover:decoration-solid" style={{ color: '#685B72' }} onClick={drill(r, 'avg_days_to_start')} data-testid={`cohort-drill-${r.month}-days`} title="Show the clients that drove this average">
                      {r.avg_days_to_start == null ? '—' : `${r.avg_days_to_start}d`}
                    </td>
                  </tr>
                  {isOpen && (r.by_source || []).map(s => (
                    <tr key={`${r.month}-${s.source}`} className="border-t" style={{ borderColor: '#F1EDF5', background: '#FBF9FC' }} data-testid={`cohort-src-${r.month}-${s.source.replace(/\W+/g,'_')}`}>
                      <td className="px-2 py-1.5 w-8" />
                      <td className="px-3 py-1.5 text-center text-[11px] italic" style={{ color: '#685B72' }}>{s.source}</td>
                      <td className="px-3 py-1.5 text-center font-mono text-[11px] underline decoration-dotted underline-offset-2 hover:decoration-solid cursor-pointer" style={{ color: '#685B72' }} onClick={drill(r, 'referrals', s.source)} data-testid={`cohort-drill-${r.month}-${s.source.replace(/\W+/g,'_')}-referrals`}>{s.referrals}</td>
                      <td className="px-3 py-1.5 text-center font-mono text-[11px] underline decoration-dotted underline-offset-2 hover:decoration-solid cursor-pointer" style={_pctColor(s.evals_scheduled_pct)} onClick={drill(r, 'evals_scheduled', s.source)} data-testid={`cohort-drill-${r.month}-${s.source.replace(/\W+/g,'_')}-evals`}>
                        {s.evals_scheduled_pct}% <span className="text-[10px] opacity-70">({s.evals_scheduled}/{s.referrals})</span>
                      </td>
                      <td className="px-3 py-1.5 text-center font-mono text-[11px] underline decoration-dotted underline-offset-2 hover:decoration-solid cursor-pointer" style={_pctColor(s.therapy_started_pct)} onClick={drill(r, 'therapy_started', s.source)} data-testid={`cohort-drill-${r.month}-${s.source.replace(/\W+/g,'_')}-therapy`}>
                        {s.therapy_started_pct}% <span className="text-[10px] opacity-70">({s.therapy_started}/{s.referrals})</span>
                      </td>
                      <td className="px-3 py-1.5 text-center font-mono text-[11px] w-24 underline decoration-dotted underline-offset-2 hover:decoration-solid cursor-pointer" style={{ color: '#685B72' }} onClick={drill(r, 'avg_days_to_start', s.source)} data-testid={`cohort-drill-${r.month}-${s.source.replace(/\W+/g,'_')}-days`}>
                        {s.avg_days_to_start == null ? '—' : `${s.avg_days_to_start}d`}
                      </td>
                    </tr>
                  ))}
                </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

// ── Conversion funnel ───────────────────────────────────────────────────
function ConversionFunnel({ funnel, byClinic }) {
  const { openDrilldown } = useDrilldown();
  const clinicOptions = Object.keys(byClinic || {}).sort();
  // When the response is already scoped to a single clinic (e.g. an OD in
  // "My Location" mode), the "All clinics" option is redundant — it and
  // the one clinic key resolve to identical numbers, which felt like a
  // broken toggle to ODs. In that case we drop the dropdown entirely and
  // pin scope to that single clinic so the header + numbers are honest.
  const singleClinic = clinicOptions.length === 1 ? clinicOptions[0] : null;
  const [scope, setScope] = useState(singleClinic || 'all');
  // If the parent re-renders with a different set of clinics (scope toggle
  // above changed from "mine" → "group"), sync so we don't get stuck on a
  // stale selection.
  useEffect(() => {
    if (singleClinic) {
      setScope(singleClinic);
    } else if (scope !== 'all' && !clinicOptions.includes(scope)) {
      setScope('all');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [singleClinic, clinicOptions.join('|')]);

  const stages = scope === 'all' ? funnel : (byClinic[scope] || []);
  if (!stages || stages.length === 0) return null;
  const max = Math.max(1, ...stages.map(s => s.count));
  const first = stages[0]?.count || 0;
  const shades = ['#685B72', '#685B72', '#685B72', '#685B72', '#839E97'];
  const showDropdown = clinicOptions.length > 1;
  const headerSuffix = singleClinic
    ? `(Where families drop off — ${singleClinic} over the last 12 months)`
    : scope === 'all'
      ? '(Where families drop off — org-wide over the last 12 months)'
      : `(Where families drop off — ${scope} over the last 12 months)`;
  return (
    <Card className="rounded-2xl border p-0 overflow-hidden" style={{ borderColor: C.border }} data-testid="conversion-funnel">
      <div className="px-4 py-2 flex items-center justify-between gap-2 text-white" style={{ background: '#839E97' }}>
        <div className="flex items-baseline gap-2">
          <span className="text-[10px] uppercase tracking-widest font-bold">Conversion Funnel</span>
          <span className="text-[10px] opacity-70">{headerSuffix}</span>
          <InfoTooltip testId="tt-conversion-funnel" text="Five drop-off stages: Referral → Eval scheduled → Eval completed → Auth cleared → Therapy started. The funnel follows each client's Main Clinic — even if the eval or therapy happened elsewhere." className="text-white/70 hover:text-white" />
        </div>
        {showDropdown && (
          <select
            value={scope}
            onChange={e => setScope(e.target.value)}
            className="text-[11px] rounded-md px-2 py-1 bg-white/10 text-white border border-white/20 hover:bg-white/20 cursor-pointer"
            data-testid="funnel-scope"
          >
            <option value="all" className="text-[#2D2833]">All clinics</option>
            {clinicOptions.map(cn => (
              <option key={cn} value={cn} className="text-[#2D2833]">{cn}</option>
            ))}
          </select>
        )}
      </div>
      <p className="px-5 pt-3 text-[11px]" style={{ color: '#685B72' }} data-testid="conversion-funnel-desc">
        This funnel shows how many families move through each stage — from first inquiry all the way to therapy started. The wider the bar, the more families are at that step; the drop-offs highlight where you're losing them.
      </p>
      <div className="p-5 space-y-2.5">
        {stages.map((s, i) => {
          const widthPct = Math.max(4, Math.round(100 * s.count / max));
          const ofFirst = first > 0 ? Math.round(1000 * s.count / first) / 10 : 0;
          const dropFromPrev = i === 0 ? null
            : (stages[i - 1].count > 0 ? Math.round(1000 * (stages[i - 1].count - s.count) / stages[i - 1].count) / 10 : null);
          // Map stage → metric for drill-in
          const stageMetric = ['referrals', 'evals_scheduled', 'evals_completed', 'auth_received', 'therapy_started'][i] || 'referrals';
          // Same 12-month window as the funnel aggregation
          const now = new Date();
          const endMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
          const sDate = new Date(now.getFullYear(), now.getMonth() - 11, 1);
          const startMonth = `${sDate.getFullYear()}-${String(sDate.getMonth() + 1).padStart(2, '0')}`;
          const onStageClick = () => {
            if (!s.count) return;
            openDrilldown({
              title: `${s.stage} — last 12 months`,
              subtitle: `${scope === 'all' ? 'All clinics' : scope}`,
              endpoint: '/drilldown/referral-cohort',
              params: {
                start_month: startMonth,
                end_month: endMonth,
                metric: stageMetric,
                clinic_attribution: 'main',
                ...(scope !== 'all' ? { scope } : {}),
              },
              rowNav: (r) => (r?.client_id ? `/pipeline?open=${r.client_id}` : null),
            });
          };
          return (
            <div
              key={s.stage}
              data-testid={`funnel-stage-${i}`}
              onClick={onStageClick}
              className={s.count ? 'cursor-pointer hover:bg-[#FBF8F4] rounded-md px-2 -mx-2 py-1 transition' : ''}
              title={s.count ? 'Click to see the clients at this stage' : undefined}
            >
              <div className="flex items-baseline justify-between text-[12px] mb-1">
                <div className="font-medium" style={{ color: '#2D2833' }}>
                  <span className="inline-block w-6 text-[10px] font-mono" style={{ color: '#685B72' }}>{i + 1}.</span>
                  {s.stage}
                </div>
                <div className="flex items-baseline gap-3">
                  {dropFromPrev != null && dropFromPrev > 0 && (
                    <span className="text-[10px] font-semibold" style={{ color: '#8A3A3A' }}>−{dropFromPrev}%</span>
                  )}
                  <span className="text-[11px]" style={{ color: '#685B72' }}>{ofFirst}% of referrals</span>
                  <span className="font-mono font-semibold underline decoration-dotted underline-offset-2" style={{ color: '#2D2833' }}>{s.count.toLocaleString()}</span>
                </div>
              </div>
              <div className="h-6 rounded-md overflow-hidden" style={{ background: '#F1EDF5' }}>
                <div
                  className="h-full flex items-center justify-end pr-2 transition-all"
                  style={{
                    width: `${widthPct}%`,
                    background: shades[i % shades.length],
                    color: '#FFFFFF',
                  }}
                >
                  <span className="text-[10px] font-mono">{widthPct}%</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
